import { ExceptionUtils } from "#src/utils/exception";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "@validereinc/common-components";
import {
  ActivityAdapter,
  ActivityResource,
  type ActivityResourceType,
  type AssetGroupType,
  type DeviceType,
  type EquipmentType,
  type FacilityType,
  type FlowType,
  Resources,
  type RoleType,
  type UserGroupType,
} from "@validereinc/domain";
import { BaseError, downloadLink } from "@validereinc/utilities";
import { useMemo } from "react";
import { DEFAULT_QUERY_OPTIONS } from "./adapterUtils";
import { useListEquipment } from "./useEquipment";
import { useListFacilities } from "./useFacilities";
import { useListFlows } from "./useFlows";
import { useListNetworks } from "./useNetworks";
import { useListRoles } from "./useRoles";
import { useListUserGroups } from "./useUserGroups";
import { getResourceAdapterAndResource } from "./utils/getResourceAdapterAndResource";
import { useListDevices } from "./useDevices";

export const useListActivityLogs = (
  params: Parameters<typeof ActivityAdapter.getList>[0],
  options: { enabled?: boolean } = {}
) =>
  useQuery({
    queryKey: [...Resources.ACTIVITY, params],
    queryFn: () => ActivityAdapter.getList(params),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useExportActivityLogs = (
  params: Parameters<typeof ActivityAdapter.exportList>[0] = {}
) => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const report = await ActivityAdapter?.exportList?.(params);
      if (!report?.s3_download_link) {
        throw new BaseError(
          `Could not fetch download URL for activity log export`,
          { cause: report }
        );
      }
      downloadLink(report.s3_download_link, report.name);
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({
        intent: "error",
        description: "Failed to export activity log.",
      });
    },
  });
};

/**
 * A hook that fetches resource data based on the asset type and resource IDs
 */
export const useAssetResourceQuery = ({
  assetType,
  resourceIds,
}: {
  assetType: Extract<
    ActivityResourceType,
    "FACILITY" | "FLOW" | "ASSET_GROUP" | "EQUIPMENT" | "DEVICE"
  >;
  resourceIds: string[];
}): {
  resourceIdsToResourceDetailsMap: Record<
    string,
    FacilityType | FlowType | AssetGroupType | EquipmentType | DeviceType
  >;
  isLoadingResources: boolean;
} => {
  const { adapter, resource } = getResourceAdapterAndResource(assetType);

  const { data: resourcesQueryData, isLoading: isLoadingResources } = useQuery<
    Awaited<ReturnType<typeof adapter.getList>>,
    unknown,
    Awaited<ReturnType<typeof adapter.getList>>,
    [typeof resource, typeof resourceIds]
  >({
    queryKey: [resource, resourceIds],

    queryFn: () =>
      adapter.getList({
        filters: { id: resourceIds },
      }),
    enabled: Boolean(resourceIds.length),
    ...DEFAULT_QUERY_OPTIONS,
  });

  const resources = resourcesQueryData?.data ?? [];
  const resourceIdsToResourceDetailsMap = useMemo(() => {
    return Object.fromEntries(
      resources.map(
        (resource) =>
          [resource.id, resource] satisfies [
            string,
            FacilityType | FlowType | EquipmentType | DeviceType,
          ]
      )
    ) as Record<string, FacilityType | FlowType | EquipmentType | DeviceType>;
  }, [resources]);

  return {
    resourceIdsToResourceDetailsMap,
    isLoadingResources,
  };
};

/**
 * A hook that fetches all resource data based on the resource IDs
 */
export const useAllResourceQuery = ({
  resourceIds,
}: {
  resourceIds: Map<string, Set<string>>;
}): {
  resourceIdsToResourceDetailsMap: Record<
    string,
    | FacilityType
    | FlowType
    | AssetGroupType
    | EquipmentType
    | DeviceType
    | UserGroupType
    | RoleType
  >;
  isLoadingResources: boolean;
} => {
  const facilityIds = resourceIds.get(ActivityResource.FACILITY);
  const flowIds = resourceIds.get(ActivityResource.FLOW);
  const assetGroupIds = resourceIds.get(ActivityResource.ASSET_GROUP);
  const equipmentIds = resourceIds.get(ActivityResource.EQUIPMENT);
  const deviceIds = resourceIds.get(ActivityResource.DEVICE);
  const userGroupIds = resourceIds.get(ActivityResource.USER_GROUP);
  const roleIds = resourceIds.get(ActivityResource.ROLE);

  const { data: facilitiesQueryData, isLoading: isLoadingFacilities } =
    useListFacilities(
      {
        filters: {
          id: Array.from(facilityIds ?? []),
        },
      },
      { enabled: Boolean(facilityIds?.size) }
    );
  const facilities = facilitiesQueryData?.data ?? [];

  const { data: flowsQueryData, isLoading: isLoadingFlows } = useListFlows(
    {
      filters: {
        id: Array.from(flowIds ?? []),
      },
    },
    { enabled: Boolean(flowIds?.size) }
  );
  const flows = flowsQueryData?.data ?? [];

  const { data: assetGroupsQueryData, isLoading: isLoadingAssetGroups } =
    useListNetworks(
      {
        filters: {
          id: Array.from(assetGroupIds ?? []),
        },
      },
      { enabled: Boolean(assetGroupIds?.size) }
    );
  const assetGroups = assetGroupsQueryData?.data ?? [];

  const { data: equipmentQueryData, isLoading: isLoadingEquipment } =
    useListEquipment(
      {
        filters: {
          id: Array.from(equipmentIds ?? []),
        },
      },
      { enabled: Boolean(equipmentIds?.size) }
    );
  const equipment = equipmentQueryData?.data ?? [];

  const { data: deviceQueryData, isLoading: isLoadingDevice } = useListDevices(
    {
      filters: {
        id: Array.from(deviceIds ?? []),
      },
    },
    { enabled: Boolean(deviceIds?.size) }
  );
  const device = deviceQueryData?.data ?? [];

  const { data: userGroupsQueryData, isLoading: isLoadingUserGroups } =
    useListUserGroups(
      {
        filters: {
          id: Array.from(userGroupIds ?? []),
        },
      },
      { enabled: Boolean(userGroupIds?.size) }
    );
  const userGroups = userGroupsQueryData?.data ?? [];

  const { data: rolesQueryData, isLoading: isLoadingRoles } = useListRoles(
    {
      filters: {
        id: Array.from(roleIds ?? []),
      },
    },
    { enabled: Boolean(roleIds?.size) }
  );
  const roles = rolesQueryData?.data ?? [];

  const resources = [
    ...facilities,
    ...flows,
    ...assetGroups,
    ...equipment,
    ...device,
    ...userGroups,
    ...roles,
  ];

  const resourceIdsToResourceDetailsMap = useMemo(() => {
    return Object.fromEntries(
      resources.map(
        (resource) =>
          [resource.id, resource] satisfies [
            string,
            (
              | FacilityType
              | FlowType
              | AssetGroupType
              | EquipmentType
              | DeviceType
              | UserGroupType
              | RoleType
            ),
          ]
      )
    ) as Record<
      string,
      | FacilityType
      | FlowType
      | AssetGroupType
      | EquipmentType
      | DeviceType
      | UserGroupType
      | RoleType
    >;
  }, [resources]);

  const isLoadingResources =
    (Boolean(facilityIds?.size) && isLoadingFacilities) ||
    (Boolean(flowIds?.size) && isLoadingFlows) ||
    (Boolean(assetGroupIds?.size) && isLoadingAssetGroups) ||
    (Boolean(equipmentIds?.size) && isLoadingEquipment) ||
    (Boolean(deviceIds?.size) && isLoadingDevice) ||
    (Boolean(roleIds?.size) && isLoadingRoles) ||
    (Boolean(userGroupIds?.size) && isLoadingUserGroups);

  return {
    resourceIdsToResourceDetailsMap,
    isLoadingResources,
  };
};
